import { useState, useEffect } from 'react';
import Pagination from "../components/Pagination.jsx";
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle,
  XCircle,
  Star,
  StarOff,
  Trash2,
  Edit,
  Plus,
  Eye,
  Clock,
  User,
  MessageCircle,
  AlertCircle,
  CheckSquare,
  Square,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import {
  AdminGetPendingTestimonials,
  AdminApproveTestimonial,
  AdminRejectTestimonial,
  AdminFeatureTestimonial,
  AdminGetAllApprovedTestimonials,
  AdminGetPendingTestimonialCount,
  AdminDeleteUserTestimonial,
  CreateTesomonial,
  UpdateTesomonial,
  AllTesomonial,
  DeleteTesomonial
} from '../components/Api.jsx';

export default function UserTestimonialManager() {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  const [activeTab, setActiveTab] = useState('pending'); // pending, approved, general
  const [userTestimonials, setUserTestimonials] = useState([]);
  const [generalTestimonials, setGeneralTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(''); // approve, reject, feature
  const [adminComments, setAdminComments] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Form states for general testimonials
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    text: '',
    rating: 5
  });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setIsLoading(true);
    }

    try {
      if (activeTab === 'pending') {
        const response = await AdminGetPendingTestimonials();
        setUserTestimonials(response.data.testimonials || []);
      } else if (activeTab === 'approved') {
        const response = await AdminGetAllApprovedTestimonials();
        setUserTestimonials(response.data.testimonials || []);
      } else if (activeTab === 'general') {
        const response = await AllTesomonial();
        setGeneralTestimonials(response.data || []);
      }

      // Always fetch pending count
      const countResponse = await AdminGetPendingTestimonialCount();
      setPendingCount(countResponse.data.pendingCount || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const handleRefresh = () => {
    fetchData(true);
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const handleAction = async (type) => {
    setActionType(type);
    setAdminComments('');
    setShowActionModal(true);
  };

  const confirmAction = async () => {
    try {
      const testimonial = selectedTestimonial;

      if (actionType === 'approve') {
        await AdminApproveTestimonial(testimonial.id, adminComments);
      } else if (actionType === 'reject') {
        await AdminRejectTestimonial(testimonial.id, adminComments);
      } else if (actionType === 'feature') {
        const isCurrentlyFeatured = testimonial.isFeatured;
        await AdminFeatureTestimonial(testimonial.id, !isCurrentlyFeatured);
      }

      setShowActionModal(false);
      showToast(`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} action successful`);
      fetchData();
    } catch (error) {
      console.error('Error performing action:', error);
      showToast(`Failed to ${actionType} testimonial. Please try again.`, 'error');
    }
  };

  const handleDelete = (id) => {
    setConfirmModal({
      show: true,
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this testimonial? This action cannot be undone.',
      onConfirm: async () => {
        try {
          if (activeTab === 'general') {
            await DeleteTesomonial(id);
          } else {
            await AdminDeleteUserTestimonial(id);
          }
          showToast('Testimonial deleted successfully');
          fetchData();
        } catch (error) {
          console.error('Error deleting testimonial:', error);
          showToast('Failed to delete testimonial. Please try again.', 'error');
        } finally {
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      }
    });
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        className={`${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  const getTestimonialTypeDisplay = (type) => {
    switch (type) {
      case 'TRAINER_SPECIFIC': return 'Trainer Feedback';
      case 'WORKOUT_PLAN': return 'Workout Program';
      case 'DIET_PLAN': return 'Diet Plan';
      case 'GENERAL': return 'General Experience';
      default: return type;
    }
  };

  const renderTestimonialCard = (testimonial, isGeneral = false) => (
    <motion.div
      key={testimonial.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-lg shadow-md border ${isGeneral ? 'border-gray-200' :
        testimonial.isApproved ? 'border-green-200' : 'border-orange-200'
        } p-6 mb-4 hover:shadow-lg transition-shadow`}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${isGeneral ? 'bg-blue-500' : testimonial.isVerified ? 'bg-green-500' : 'bg-gray-500'
            }`}>
            {isGeneral ? testimonial.name?.charAt(0)?.toUpperCase() : testimonial.initials || 'U'}
          </div>
          <div>
            <h3 className="font-semibold text-lg">{isGeneral ? testimonial.name : testimonial.displayName}</h3>
            {isGeneral ? (
              <p className="text-gray-600">{testimonial.role}</p>
            ) : (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">{getTestimonialTypeDisplay(testimonial.testimonialType)}</span>
                {testimonial.isVerified && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Verified
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center">
            {renderStars(isGeneral ? testimonial.rating : testimonial.rating)}
          </div>
          {!isGeneral && (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${testimonial.isApproved ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
              }`}>
              {testimonial.isApproved ? 'Approved' : 'Pending'}
            </span>
          )}
        </div>
      </div>

      {testimonial.title && (
        <h4 className="font-semibold text-lg mb-2 text-blue-600">{testimonial.title}</h4>
      )}

      <p className="text-gray-700 mb-4 leading-relaxed">
        "{testimonial.testimonialText || testimonial.text}"
      </p>

      <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
        <span>Submitted: {new Date(testimonial.createdAt || testimonial.submittedAt).toLocaleDateString()}</span>
        {testimonial.trainerName && (
          <span>Trainer: {testimonial.trainerName}</span>
        )}
      </div>

      <div className="flex justify-end space-x-2">
        <button
          onClick={() => {
            setSelectedTestimonial(testimonial);
            setShowDetailModal(true);
          }}
          className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
        >
          <Eye size={16} />
          <span>View</span>
        </button>

        {isGeneral ? (
          <>
            <button
              onClick={() => {
                setEditingId(testimonial.id);
                setFormData({
                  name: testimonial.name,
                  role: testimonial.role,
                  text: testimonial.text,
                  rating: testimonial.rating
                });
                setIsPopupOpen(true);
              }}
              className="flex items-center space-x-1 px-3 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
            >
              <Edit size={16} />
              <span>Edit</span>
            </button>
          </>
        ) : (
          <>
            {!testimonial.isApproved && (
              <>
                <button
                  onClick={() => {
                    setSelectedTestimonial(testimonial);
                    handleAction('approve');
                  }}
                  className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                >
                  <CheckCircle size={16} />
                  <span>Approve</span>
                </button>
                <button
                  onClick={() => {
                    setSelectedTestimonial(testimonial);
                    handleAction('reject');
                  }}
                  className="flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                >
                  <XCircle size={16} />
                  <span>Reject</span>
                </button>
              </>
            )}
            {testimonial.isApproved && (
              <button
                onClick={() => {
                  setSelectedTestimonial(testimonial);
                  handleAction('feature');
                }}
                className={`flex items-center space-x-1 px-3 py-1 rounded transition-colors ${testimonial.isFeatured
                  ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                {testimonial.isFeatured ? <Star size={16} /> : <StarOff size={16} />}
                <span>{testimonial.isFeatured ? 'Unfeature' : 'Feature'}</span>
              </button>
            )}
          </>
        )}

        <button
          onClick={() => handleDelete(testimonial.id)}
          className="flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
        >
          <Trash2 size={16} />
          <span>Delete</span>
        </button>
      </div>
    </motion.div>
  );

  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await UpdateTesomonial(editingId, formData.name, formData.role, formData.text, formData.rating);
        showToast('Testimonial updated successfully');
      } else {
        await CreateTesomonial(formData.name, formData.role, formData.text, formData.rating);
        showToast('Testimonial created successfully');
      }
      setFormData({ name: '', role: '', text: '', rating: 5 });
      setEditingId(null);
      setIsPopupOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      showToast('Failed to save testimonial. Please try again.', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-white text-black p-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          }`}>
          <div className="flex items-center gap-2">
            {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
            {toast.message}
          </div>
        </div>
      )}
      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-amber-100 p-3 rounded-lg">
                  <MessageCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    Add, edit, delete the feedback given by the users. Manage user testimonials, approve pending reviews, and feature testimonials for better visibility.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                {pendingCount} Pending Approval
              </div>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh testimonials"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              {activeTab === 'general' && (
                <button
                  onClick={() => {
                    setEditingId(null);
                    setFormData({ name: '', role: '', text: '', rating: 5 });
                    setIsPopupOpen(true);
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition-colors"
                >
                  <Plus size={20} />
                  <span className="ml-2">Add General Testimonial</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">

        {/* Tabs */}
        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {[
                  { id: 'pending', label: 'Pending Approval', icon: Clock, count: pendingCount },
                  { id: 'approved', label: 'Approved', icon: CheckSquare },
                  { id: 'general', label: 'General Testimonials', icon: Star }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <tab.icon size={20} />
                    <span>{tab.label}</span>
                    {tab.count > 0 && (
                      <span className="bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs font-medium">
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto py-8">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          ) : (
            <div className="grid gap-6">
              <AnimatePresence>
                {(activeTab === 'general' ? generalTestimonials : userTestimonials).slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((testimonial) =>
                  renderTestimonialCard(testimonial, activeTab === 'general')
                )}
              </AnimatePresence>

              {((activeTab === 'general' ? generalTestimonials : userTestimonials).length === 0) && (
                <div className="text-center py-12 text-gray-500">
                  <MessageCircle size={48} className="mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium">
                    {activeTab === 'pending'
                      ? 'No testimonials pending approval'
                      : activeTab === 'approved'
                        ? 'No approved testimonials'
                        : 'No general testimonials'}
                  </h3>
                  <p className="mt-2">
                    {activeTab === 'general' && 'Click the "Add General Testimonial" button to create one.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {showDetailModal && selectedTestimonial && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-2xl font-bold">Testimonial Details</h2>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle size={24} />
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                      {selectedTestimonial.displayName?.charAt(0)?.toUpperCase() ||
                        selectedTestimonial.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">
                        {selectedTestimonial.displayName || selectedTestimonial.name}
                      </h3>
                      <p className="text-gray-600">
                        {selectedTestimonial.role || getTestimonialTypeDisplay(selectedTestimonial.testimonialType)}
                      </p>
                    </div>
                  </div>

                  {selectedTestimonial.title && (
                    <div>
                      <h4 className="font-semibold text-lg text-blue-600">{selectedTestimonial.title}</h4>
                    </div>
                  )}

                  <div>
                    <div className="flex items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Rating:</span>
                      <div className="flex ml-2">{renderStars(selectedTestimonial.rating)}</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Testimonial Text:</h4>
                    <p className="text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-lg">
                      "{selectedTestimonial.testimonialText || selectedTestimonial.text}"
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <strong>Submitted:</strong> {' '}
                      {new Date(selectedTestimonial.createdAt || selectedTestimonial.submittedAt).toLocaleDateString()}
                    </div>
                    {selectedTestimonial.trainerName && (
                      <div>
                        <strong>Trainer:</strong> {selectedTestimonial.trainerName}
                      </div>
                    )}
                    {selectedTestimonial.userEmail && (
                      <div>
                        <strong>Email:</strong> {selectedTestimonial.userEmail}
                      </div>
                    )}
                    {selectedTestimonial.userName && (
                      <div>
                        <strong>Full Name:</strong> {selectedTestimonial.userName}
                      </div>
                    )}
                    {selectedTestimonial.adminComments && (
                      <div className="col-span-2">
                        <strong>Admin Comments:</strong> {selectedTestimonial.adminComments}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Action Modal */}
        {showActionModal && selectedTestimonial && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-lg shadow-xl w-full max-w-md"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-xl font-bold">
                    {actionType === 'approve' && 'Approve Testimonial'}
                    {actionType === 'reject' && 'Reject Testimonial'}
                    {actionType === 'feature' && (selectedTestimonial.isFeatured ? 'Unfeature Testimonial' : 'Feature Testimonial')}
                  </h2>
                  <button
                    onClick={() => setShowActionModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle size={24} />
                  </button>
                </div>

                <div className="mb-4">
                  <p className="text-gray-700">
                    Are you sure you want to {actionType} this testimonial from{' '}
                    <strong>{selectedTestimonial.displayName}</strong>?
                  </p>
                </div>

                {(actionType === 'approve' || actionType === 'reject') && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {actionType === 'approve' ? 'Approval Comments (optional)' : 'Rejection Reason (required)'}
                    </label>
                    <textarea
                      value={adminComments}
                      onChange={(e) => setAdminComments(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={3}
                      placeholder={actionType === 'approve' ? 'Add any comments...' : 'Explain why this testimonial is being rejected...'}
                    />
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowActionModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmAction}
                    disabled={actionType === 'reject' && !adminComments.trim()}
                    className={`px-4 py-2 rounded-lg transition-colors ${actionType === 'approve'
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : actionType === 'reject'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                      } ${actionType === 'reject' && !adminComments.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {actionType === 'approve' && 'Approve'}
                    {actionType === 'reject' && 'Reject'}
                    {actionType === 'feature' && (selectedTestimonial.isFeatured ? 'Unfeature' : 'Feature')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* General Testimonial Form Modal */}
        {isPopupOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-lg shadow-xl w-full max-w-md"
            >
              <div className="p-6">
                <h2 className="text-xl font-bold mb-4">
                  {editingId ? 'Edit General Testimonial' : 'Add General Testimonial'}
                </h2>
                <form onSubmit={handleGeneralSubmit} className="space-y-4">
                  <div>
                    <input
                      type="text"
                      placeholder="Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Role"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <textarea
                      placeholder="Testimonial Text"
                      value={formData.text}
                      onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={3}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="rating">Rating :</label>
                    <select
                      value={formData.rating}
                      onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={1}>1 Star</option>
                      <option value={2}>2 Stars</option>
                      <option value={3}>3 Stars</option>
                      <option value={4}>4 Stars</option>
                      <option value={5}>5 Stars</option>
                    </select>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setIsPopupOpen(false)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                    >
                      {editingId ? 'Update' : 'Add'} Testimonial
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </div>
      {/* Custom Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4 backdrop-blur-sm transition-all duration-300">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl transform transition-all duration-300 scale-100 p-6 border border-gray-100">
            <div className="flex flex-col items-center text-center">
              <div className="bg-red-50 p-4 rounded-full mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {confirmModal.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                {confirmModal.message}
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmModal.onConfirm}
                  className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-lg shadow-red-200 transition-all duration-200 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
          {(activeTab === 'general' ? generalTestimonials : userTestimonials)?.length > 0 && (

      <Pagination 
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={Math.ceil((Array.isArray((activeTab === 'general' ? generalTestimonials : userTestimonials)) ? (activeTab === 'general' ? generalTestimonials : userTestimonials).length : ((activeTab === 'general' ? generalTestimonials : userTestimonials)?.length || 0)) / itemsPerPage)}
          totalElements={(Array.isArray((activeTab === 'general' ? generalTestimonials : userTestimonials)) ? (activeTab === 'general' ? generalTestimonials : userTestimonials).length : ((activeTab === 'general' ? generalTestimonials : userTestimonials)?.length || 0))}
          pageSize={itemsPerPage}
          setPageSize={setItemsPerPage}
        />
      )}
</div>
  );
}